import { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { useLocation } from 'react-router-dom'

import type { PropsWithChildren, ReactElement } from 'react'
import type { Action, IAnalyticsContext, IStateReducer, TFilters } from '~/types/analytics-context'

import { DEFAULT_DAI_SINGLE_VALUE, DEFAULT_DATE_RANGE } from '~/const'
import {
  computeFilters,
  computeFiltersDate,
  DEFAULT_COUNTRY,
  DEFAULT_KPI,
  isBusinessReportsFeature,
} from '~/utils/analytics-context'
import dayjs from '~/utils/dayjs'
import { getFiltersNonEmptyField, getFiltersParamsByNames } from '~/utils/filters'

const reducer = (state: IStateReducer, action: Action): IStateReducer => {
  switch (action.type) {
    case 'SET_DISPLAYED_FILTERS':
      return {
        ...state,
        displayedFilters: {
          ...state.displayedFilters,
          ...action.payload,
        },
      }

    case 'SET_FILTERS':
      // NOTE: reset abtest_key values
      if (action.payload.breakdown && action.payload.breakdown?.[0] !== 'abtest') {
        action.payload = {
          ...action.payload,
          abtestKey: [],
        }
      }

      // NOTE: reset placement_key values
      if (action.payload.breakdown && action.payload.breakdown?.[0] !== 'dsv3') {
        action.payload = {
          ...action.payload,
          navigationGraphId: [],
          placementKey: [],
        }
      }

      const newFilters: TFilters = { ...state.filters, ...action.payload }
      const newComputedFilters = computeFilters(newFilters)
      const newComputedFiltersDate = computeFiltersDate(newFilters)

      return {
        ...state,
        computedFilters: newComputedFilters,
        computedFiltersDate: newComputedFiltersDate,
        filters: newFilters,
      }

    case 'SET_FEATURE':
      return {
        ...state,
        feature: action.payload,
      }

    case 'RESET_FILTERS':
      return {
        ...state,
        computedFilters: defaultState.computedFilters,
        computedFiltersDate: defaultState.computedFiltersDate,
        filters: defaultState.filters,
      }

    case 'SET_FILTERS_COLLAPSED':
      return {
        ...state,
        isFiltersCollapsed: action.payload,
      }

    default:
      return state
  }
}

////////////////////////////////////////////////////
// INITIAL STATE
////////////////////////////////////////////////////

export const defaultState: IStateReducer = {
  computedFilters: computeFilters({
    abtestKey: [],
    appKey: [],
    country: [],
    dai: DEFAULT_DAI_SINGLE_VALUE,
    deviceCategory: [],
    deviceType: [],
    installationAppVersion: [],
    installationCountry: [DEFAULT_COUNTRY],
    kpi: [DEFAULT_KPI],
    navigationGraphId: [],
    placementKey: [],
    uaSource: [],
    userType: [],
  }),
  computedFiltersDate: {
    range: DEFAULT_DATE_RANGE,
  },
  displayedFilters: {
    datePicker: false,
    dropdownAppKey: false,
    dropdownBreakdown: false,
    dropdownCountry: false,
    dropdownDai: false,
    dropdownDeviceCategory: false,
    dropdownDeviceType: false,
    dropdownInstallationAppVersion: false,
    dropdownInstallationCountry: false,
    dropdownUaSource: false,
    dropdownUserType: false,
  },
  feature: '',
  filters: {
    abtestKey: [],
    appKey: [],
    breakdown: [],
    country: [],
    dai: DEFAULT_DAI_SINGLE_VALUE,
    deviceCategory: [],
    deviceType: [],
    endDate: [DEFAULT_DATE_RANGE.end],
    installationAppVersion: [],
    installationCountry: [DEFAULT_COUNTRY],
    kpi: [DEFAULT_KPI],
    navigationGraphId: [],
    placementKey: [],
    startDate: [DEFAULT_DATE_RANGE.start],
    uaSource: [],
    userType: [],
  },
}

const getInitialState = (filters: TFilters, displayedFilters: IStateReducer['displayedFilters']) => {
  return {
    ...defaultState,
    computedFilters: computeFilters(filters),
    computedFiltersDate: {
      ...defaultState.computedFiltersDate,
      ...computeFiltersDate(filters),
    },
    displayedFilters: {
      ...defaultState.displayedFilters,
      ...displayedFilters,
    },
    feature: defaultState.feature,
    filters: {
      ...defaultState.filters,
      ...filters,
    },
  }
}

const useAnalyticsFilters = () => {
  const { search } = useLocation()

  const filters = getFiltersParamsByNames(search, Object.keys(defaultState.filters))

  return getFiltersNonEmptyField(filters)
}

const AnalyticsContext = createContext<IAnalyticsContext>({
  dispatch: (): IStateReducer => defaultState,
  state: defaultState,
})

const AnalyticsContextProvider = ({
  children,
  displayedFilters,
}: PropsWithChildren<{
  displayedFilters: Record<
    'analytics-aggregated' | 'business-reports',
    Record<string, IStateReducer['displayedFilters']>
  >
}>): ReactElement => {
  const filters = useAnalyticsFilters()

  const locationSplitted = location.pathname.split('/')

  const filtersToDisplay =
    displayedFilters[location.pathname.includes('ca') ? 'analytics-aggregated' : 'business-reports'][
      locationSplitted.length > 3 ? locationSplitted[3] : ''
    ]

  const initialState = getInitialState(filters, filtersToDisplay)

  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (state.feature && isBusinessReportsFeature(state.feature)) {
      dispatch({
        payload: {
          startDate: [dayjs(new Date()).subtract(2, 'year').format('YYYY-MM-DD')],
        },
        type: 'SET_FILTERS',
      })
    }
  }, [state.feature])

  const contextAnalytics = useMemo(() => ({ dispatch, state }), [state])

  return <AnalyticsContext.Provider value={contextAnalytics}>{children}</AnalyticsContext.Provider>
}

// context consumer hook
const useAnalyticsContext = (): IAnalyticsContext => {
  const context = useContext(AnalyticsContext)

  if (context === undefined) {
    throw new Error('useAnalyticsContext was used outside of its Provider')
  }

  return context
}

export { AnalyticsContextProvider, useAnalyticsContext }
